import { useEffect, useRef, useState } from 'react';
import { Copy, Eye, EyeOff, SignalLow, SignalMedium, SignalHigh } from 'lucide-react';
import { Badge, Button, Deadline, EntityText, Money } from '@/components/ui';
import { EntityChipIcon } from '@/components/ui/entityIcons';
import { FileChip } from '@/components/ui/FileChip';
import { displayDateInput } from '@/components/ui/fieldData';
import { formatDateTime } from '@/lib/dataFormat';
import {
  validateFieldValue,
  type FieldDefinition,
  type TypedFieldValue,
  type FieldOption,
} from './typedFieldData';
import type { FieldRegistry, FieldActions, SecretAccess, CustomDefinition } from './fieldRegistry';

export function ImportanceValue({
  option,
  firstLineBaseline = false,
}: {
  option: FieldOption;
  firstLineBaseline?: boolean;
}) {
  const Icon = option.level === 3 ? SignalHigh : option.level === 2 ? SignalMedium : SignalLow;
  return (
    <span
      className={
        firstLineBaseline ? 'inline-flex items-baseline gap-1.5' : 'inline-flex items-center gap-1.5'
      }
    >
      <Icon
        aria-hidden
        className={
          firstLineBaseline ? 'size-4 shrink-0 self-center text-fg-muted' : 'size-4 shrink-0 text-fg-muted'
        }
      />
      <span>{option.label}</span>
    </span>
  );
}
function SecretValue({ configured, access }: { configured: boolean; access?: SecretAccess }) {
  const [plain, setPlain] = useState<string | null>(null),
    [pending, setPending] = useState(false),
    [error, setError] = useState(false),
    [copied, setCopied] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => request.current?.abort(), []);
  if (!configured) return <span className="text-fg-subtle">Не задан</span>;
  const hide = () => {
    request.current?.abort();
    setPlain(null);
    setPending(false);
    setCopied(false);
    setError(false);
  };
  const reveal = async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setError(false);
    try {
      const value = await access!.reveal(controller.signal);
      if (!controller.signal.aborted) setPlain(value);
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setPending(false);
    }
  };
  return (
    <div data-slot="secret-field" className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {plain !== null && access?.canReveal ? (
          <code data-secret-value="" className="min-w-0 font-mono text-xs break-all">
            {plain}
          </code>
        ) : (
          <span aria-label="Значение скрыто" className="tracking-widest text-fg-muted">
            ••••••••
          </span>
        )}
        {access?.canReveal && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={pending}
            onClick={() => {
              if (plain !== null) hide();
              else void reveal();
            }}
            leftIcon={plain !== null ? <EyeOff /> : <Eye />}
          >
            {pending ? 'Проверяем доступ…' : plain !== null ? 'Скрыть' : 'Показать'}
          </Button>
        )}
        {plain !== null && access?.canReveal && access.copy && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            leftIcon={<Copy />}
            onClick={() => {
              void access.copy!(plain).then(
                () => setCopied(true),
                () => setError(true),
              );
            }}
          >
            {copied ? 'Скопировано' : 'Копировать'}
          </Button>
        )}
      </div>
      {!access?.canReveal && <p className="text-2xs text-fg-subtle">Нет права на раскрытие</p>}
      {error && (
        <p role="alert" className="text-xs text-danger-strong">
          Не удалось выполнить действие. Попробуй ещё раз.
        </p>
      )}
    </div>
  );
}
export function FieldValue({
  definition,
  value,
  registry = {},
  secret,
  firstLineBaseline = false,
  ...actions
}: FieldActions & {
  definition: FieldDefinition;
  value: TypedFieldValue;
  registry?: FieldRegistry;
  secret?: SecretAccess;
  firstLineBaseline?: boolean;
}) {
  if (value.value === null || value.value === '' || (Array.isArray(value.value) && value.value.length === 0))
    return (
      <span data-field-empty="" className="text-fg-subtle">
        Не заполнено
      </span>
    );
  const error = validateFieldValue({ ...definition, required: false }, value);
  if (error)
    return (
      <span className="text-danger-strong" title={error}>
        Значение недоступно
      </span>
    );
  switch (value.type) {
    case 'text':
    case 'multiline':
      return <span className="break-words whitespace-pre-wrap">{value.value}</span>;
    case 'number':
      return (
        <span className="tabular-nums">
          {new Intl.NumberFormat('ru-RU', { maximumSignificantDigits: 21 })
            .format(value.value)
            .replace(/^-/, '−')}
        </span>
      );
    case 'money':
      return <Money minorUnits={value.value.minorUnits} currency={value.value.currency} />;
    case 'date':
      return (
        <time dateTime={value.value} className="tabular-nums">
          {displayDateInput(value.value)}
        </time>
      );
    case 'period':
      return (
        <span className="tabular-nums">
          {displayDateInput(value.value.from)} — {displayDateInput(value.value.to)}
        </span>
      );
    case 'deadline': {
      const zone =
        definition.type === 'deadline' ? (definition.timeZone ?? 'Europe/Moscow') : 'Europe/Moscow';
      return (
        <div className="space-y-1">
          <Deadline at={value.value} timeZone={zone} />
          <p className="text-2xs text-fg-muted tabular-nums">
            {formatDateTime(value.value, zone)} · {zone === 'Europe/Moscow' ? 'МСК' : zone}
          </p>
        </div>
      );
    }
    case 'person':
    case 'entity':
      return (
        <span
          className={
            firstLineBaseline
              ? 'inline-flex min-w-0 items-baseline gap-1.5'
              : 'inline-flex min-w-0 items-center gap-1.5'
          }
        >
          <EntityChipIcon
            kind={value.value.kind ?? (value.type === 'person' ? 'user' : 'task')}
            className={
              firstLineBaseline
                ? 'size-3.5 shrink-0 self-center text-fg-muted'
                : 'size-3.5 shrink-0 text-fg-muted'
            }
          />
          {actions.onEntityOpen ? (
            <EntityText name={value.value.label} onClick={() => actions.onEntityOpen!(value.value!)} />
          ) : (
            <span>{value.value.label}</span>
          )}
        </span>
      );
    case 'status':
    case 'select':
    case 'importance': {
      const option =
        'options' in definition
          ? (definition.options.find((o) => o.id === value.value) as FieldOption | undefined)
          : undefined;
      if (!option) return <span className="text-fg-subtle">Неизвестное значение: {String(value.value)}</span>;
      return value.type === 'status' ? (
        <Badge variant={option.tone ?? 'neutral'} solid>
          {option.label}
        </Badge>
      ) : value.type === 'importance' ? (
        <ImportanceValue option={option} firstLineBaseline={firstLineBaseline} />
      ) : (
        <span>{option.label}</span>
      );
    }
    case 'tags':
      return (
        <span className="flex flex-wrap gap-1.5">
          {value.value.map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </span>
      );
    case 'file':
      return (
        <div className={firstLineBaseline ? 'flex min-w-0 flex-col gap-2 self-stretch' : 'min-w-0 space-y-2'}>
          {value.value.map((file) => (
            <FileChip
              key={file.id}
              file={file}
              firstLineBaseline={firstLineBaseline}
              onOpenFrom={
                actions.onFileOpen && file.url ? (trigger) => actions.onFileOpen!(file, trigger) : undefined
              }
            />
          ))}
        </div>
      );
    case 'link':
      return (
        <a
          href={value.value}
          target="_blank"
          rel="noreferrer"
          className="break-all text-brand-fg underline decoration-dotted underline-offset-4"
        >
          {value.value}
        </a>
      );
    case 'email':
      return (
        <a
          href={'mailto:' + value.value}
          className="break-all text-brand-fg underline decoration-dotted underline-offset-4"
        >
          {value.value}
        </a>
      );
    case 'phone':
      return (
        <a
          href={'tel:' + value.value.replace(/[ ()-]/g, '')}
          className="text-brand-fg tabular-nums underline decoration-dotted underline-offset-4"
        >
          {value.value}
        </a>
      );
    case 'checkbox':
      return <span>{value.value ? 'Да' : 'Нет'}</span>;
    case 'secret':
      return (
        <SecretValue key={String(secret?.canReveal)} configured={value.value.configured} access={secret} />
      );
    default: {
      const adapter = registry[value.type];
      if (!adapter) return <span className="text-fg-subtle">Тип поля не подключён</span>;
      return <>{adapter.render(value.value, definition as CustomDefinition)}</>;
    }
  }
}
